import React, { useState } from "react";
import PropTypes from "prop-types";
import { fasIcon, numberToCurrency } from "h";

import NumberSelector from "@/checkout/view_cart/NumberSelector";
import { toKebabCase, tokenList } from "@/common/util";
import { MAX_MAILBOXES, MIN_MAILBOXES } from "@/checkout/view_cart/emailSubscription";
import { titanPlanIcon } from "@/common/titan_plans";

import "./email_plan_card.scss";

const VISIBLE_FEATURE_COUNT = 3;

// A tier's copy can lead with a summary line ("Everything in Pro, plus:")
// rather than an actual feature -- it has no checkmark and always sits
// between the visible features and the toggle, so it's carved out before the
// rest of the list is split into the shown/collapsed halves.
const isCarryoverNote = (feature) => /^Everything in .+ \+$/i.test(feature);

function splitFeatures(features, serviceType, showAllFeatures) {
  const [first, ...rest] = features;
  const hasNote = isCarryoverNote(first);
  const items = hasNote ? rest : features;
  const count = showAllFeatures
    ? items.length
    : (serviceType == "titan_pro" ? 1 : VISIBLE_FEATURE_COUNT);

  return {
    note: hasNote && !showAllFeatures ? first : null,
    visible: items.slice(0, count),
    hidden: items.slice(count)
  };
}

function FeatureItem({ feature }) {
  return (
    <li>
      <span className={"check"}>{fasIcon("check")}</span>
      {feature}
    </li>
  );
}

/**
 * One Titan tier. Quantity is local because it is throwaway UI state until the
 * customer commits it with "Add to order".
 */
function EmailPlanCard({ plan, onAdd, disabled, showQuantityControls, showAllFeatures }) {
  const [quantity, setQuantity] = useState(MIN_MAILBOXES);
  const [expanded, setExpanded] = useState(false);
  const { note, visible, hidden } = splitFeatures(plan.features, plan.serviceType, showAllFeatures);
  const monthlyPrice = numberToCurrency(Math.round(plan.price / 12))
    .replace(".00", "");

  const quantityControls = !showQuantityControls ? null : (<>
    <div className={"mailboxes-label"}>Mailboxes</div>

    <NumberSelector
      changeHandler={setQuantity}
      className={"stepper"}
      max={MAX_MAILBOXES}
      min={MIN_MAILBOXES}
      value={quantity}
    />
  </>);

  return (
    <div
      className={`email-plan-card ${plan.serviceType}`}
      data-test-id={`plan-card-${plan.serviceType}`}
    >
      {plan.tag && <div className={`tag ${toKebabCase(plan.tag)}`}>{plan.tag}</div>}

      <div className={"inner"}>
        <header>
          <h2 className={"title"}>
            <span className={"title_icon"}>
              {titanPlanIcon(plan.serviceType)}
            </span>
            Titan {plan.title}
          </h2>
          <h3 className={"description"}>{plan.description}</h3>

          <p className={"price"}>
            <strong>{monthlyPrice}/month</strong>
          </p>
          <p className={"billing"}>
            per mailbox, billed at {numberToCurrency(plan.price).replace(".00", "")} per year
          </p>
        </header>

        <div>
          {quantityControls}

          <button
            type="button"
            className={"add-plan-to-cart btn primary"}
            disabled={disabled}
            onClick={() => onAdd({ planId: plan.planId, quantity })}
          >
            {`Add to order — ${numberToCurrency(plan.price * quantity)}/yr`}
          </button>
        </div>

        <div className={"body"}>
          <ul className={"plan-features"}>
            {note && <li className={"note"}>{note}</li>}

            {visible.map((feature) => (
              <FeatureItem key={feature} feature={feature} />
            ))}

            {hidden.length > 0 && (
              <li className={"toggle"}>
                <button
                  type="button"
                  className={"toggle-button"}
                  onClick={() => setExpanded((prev) => !prev)}
                  aria-expanded={expanded}
                >
                  {expanded ? "Hide features" : "See all features"}
                  {fasIcon("chevron-down", {
                    className: tokenList({chevron: true, "chevron-up": expanded})
                  })}
                </button>
              </li>
            )}

            {expanded &&
              hidden.map((feature) => <FeatureItem key={feature} feature={feature} />)}
          </ul>
        </div>
      </div>
    </div>
  );
}

EmailPlanCard.propTypes = {
  disabled: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  showAllFeatures: PropTypes.bool,
  plan: PropTypes.shape({
    description: PropTypes.string.isRequired,
    features: PropTypes.arrayOf(PropTypes.string).isRequired,
    planId: PropTypes.number.isRequired,
    price: PropTypes.number.isRequired,
    serviceType: PropTypes.string.isRequired,
    tag: PropTypes.string,
    title: PropTypes.string.isRequired
  }).isRequired
};

export default EmailPlanCard;
